import { CatalogDictionaryEntry } from "../products/product3DTypes";
import { CatalogImageOwner } from "./catalogQueries";

type CatalogCategoryLike = {
  id?: string | null;
  name?: string | null;
  parent?: { name?: string | null } | null;
};

export function getCatalogItemDisplayName(item: CatalogImageOwner): string {
  const rawName = meaningfulText(item.name);
  // Older applies sometimes cached the Mongo id as `name`; treat that as missing.
  const name =
    rawName && rawName !== item.id.trim() ? rawName : undefined;
  return (
    name ??
    getCatalogCategoryName(item) ??
    meaningfulText(item.brand) ??
    meaningfulText(item.supplier?.name) ??
    meaningfulText(item.description) ??
    getImageFileLabel(item.images?.[0]) ??
    "Untitled product"
  );
}

export function getCatalogEntryDisplayName(
  entry: CatalogDictionaryEntry | undefined,
  fallbackId?: string,
): string | undefined {
  if (!entry && !fallbackId) return undefined;
  const entryId = entry?.id?.trim() || fallbackId?.trim();
  const rawName = meaningfulText(entry?.name);
  const name =
    rawName && (!entryId || rawName !== entryId) ? rawName : undefined;
  return (
    name ??
    entry?.categories?.map((category) => meaningfulText(category.name)).find(Boolean) ??
    meaningfulText(entry?.brand) ??
    meaningfulText(entry?.supplierName) ??
    meaningfulText(entry?.description) ??
    getImageFileLabel(entry?.imageUrl) ??
    (fallbackId && fallbackId !== entryId ? fallbackId : undefined) ??
    (entryId ? "Untitled product" : undefined)
  );
}

export function getCatalogCategorySnapshots(item: CatalogImageOwner) {
  return getCatalogCategories(item)
    ?.filter(Boolean)
    .map((category) => ({
      id: category?.id ?? undefined,
      name: category?.name ?? undefined,
      parentName: category?.parent?.name ?? undefined,
    }));
}

function getCatalogCategoryName(item: CatalogImageOwner): string | undefined {
  return getCatalogCategories(item)
    ?.map((category) => meaningfulText(category?.name))
    .find(Boolean);
}

function getCatalogCategories(
  item: CatalogImageOwner,
): Array<CatalogCategoryLike | null> | null | undefined {
  return item.categories ?? item.categoriesMeta;
}

function meaningfulText(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  if (trimmed === "." || trimmed === "-" || trimmed === "_") return undefined;
  if (trimmed.toLowerCase() === "untitled") return undefined;
  return trimmed;
}

function getImageFileLabel(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  const path = url.split("?")[0]?.split("#")[0];
  const filename = path?.split("/").filter(Boolean).pop();
  if (!filename) return undefined;
  const decoded = decodeURIComponent(filename)
    .replace(/\.[a-z0-9]+$/i, "")
    .replace(/[-_]+/g, " ")
    .trim();
  return meaningfulText(decoded);
}
